import { useState } from 'react';
import { Trophy, Medal, Award, Star } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import './Results.css';

export default function Results() {
  const [activeTab, setActiveTab] = useState('mco-2026');

  // Hardcoded Results Data
  const mco2026Winners = [
    { rank: 1, roll: 'JSM-MCO-26-471', name: 'Prince Kumar', school: 'Ramanlal Shorawala International School', score: 98.75 },
    { rank: 2, roll: 'JSM-MCO-26-635', name: 'Supratik Mahato', school: 'Delhi Public School Mathura Refinery Nagar', score: 90 },
    { rank: 3, roll: 'JSM-MCO-26-427', name: 'Nitesh Kumar', school: 'Gyan Deep Shiksha Bharati', score: 87.5 }
  ];

  const mco2026Consolation = [
    { rank: 4, roll: 'JSM-MCO-26-712', name: 'Vivaan Saraswat', school: 'Rajiv International School', score: 81.5 },
    { rank: 5, roll: 'JSM-MCO-26-598', name: 'Shobhit Agrawal', school: 'Kanha Makhan Public School Mathura', score: 78.75 },
    { rank: 6, roll: 'JSM-MCO-26-658', name: 'Tanu Agrawal', school: 'Kanha Makhan Public School Mathura', score: 78.75 },
    { rank: 7, roll: 'JSM-MCO-26-552', name: 'Sahil kumar', school: 'Gyan Deep Shiksha Bharati', score: 77.5 },
    { rank: 8, roll: 'JSM-MCO-26-680', name: 'Ujas Singh', school: 'Delhi Public School, Mathura Refinery Nagar', score: 77 },
    { rank: 9, roll: 'JSM-MCO-26-419', name: 'Nirbhay Singh', school: 'Gyandeep shiksha bharati', score: 76 },
    { rank: 10, roll: 'JSM-MCO-26-141', name: 'Chirag Sharma', school: 'Kendriya Vidyalaya Mathura Cantt', score: 75 },
    { rank: 11, roll: 'JSM-MCO-26-121', name: 'Bhumika Sharma', school: 'Shri Ji Baba Saraswati Balika Vidhya Mandir', score: 73.5 },
    { rank: 12, roll: 'JSM-MCO-26-641', name: 'Swati', school: 'Kanha makhan public school', score: 73 },
    { rank: 13, roll: 'JSM-MCO-26-359', name: 'Manish Saraswat', school: 'Ramanlal Shorawala International School', score: 72.75 }
  ];

  return (
    <div className="results-page">
      <Helmet>
        <title>Olympiad Results & Winners | Dynamic Exams</title>
        <meta name="description" content="Check the latest Olympiad results, top performers, and award winners. Celebrate the brightest minds and their incredible achievements with Dynamic Exams." />
        <meta property="og:title" content="Olympiad Results | Dynamic Exams" />
        <meta property="og:description" content="Check the latest Olympiad results, top performers, and award winners." />
      </Helmet>
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Olympiad Results</h1>
          <p className="page-subtitle">Celebrating the brightest minds and their incredible achievements.</p>
        </div>
      </div>

      <section className="section-padding">
        <div className="container results-container">
          
          {/* TABS MENU */}
          <div className="results-tabs glass-panel">
            <button 
              role="tab"
              aria-selected={activeTab === 'mco-2026'}
              className={`result-tab ${activeTab === 'mco-2026' ? 'active' : ''}`}
              onClick={() => setActiveTab('mco-2026')}
            >
              MCO - 2026 (1st Edition)
            </button>
            {/* Future olympiad tabs will go here */}
          </div>

          {/* TAB CONTENT: MCO 2026 */}
          {activeTab === 'mco-2026' && (
            <div className="result-content animate-fade-in">
              
              {/* HIGHEST PARTICIPATION TROPHY BANNER */}
              <div className="trophy-banner glass-panel premium-glow">
                <div className="trophy-icon-wrapper">
                  <Trophy size={48} className="trophy-icon text-[var(--secondary-gold)]" />
                </div>
                <div className="trophy-text">
                  <h3 className="text-[var(--secondary-gold)] font-bold uppercase tracking-widest text-sm mb-1">Highest Participation Trophy</h3>
                  <h2 className="text-3xl font-extrabold text-[var(--text-primary)]">Ratan Lal Phool Katori Devi School</h2>
                  <p className="text-[var(--text-secondary)] mt-2">Awarded for outstanding enthusiasm and the highest student enrollment in the Mathura Computer Olympiad (1st Edition).</p>
                </div>
              </div>

              <div className="results-grid">
                
                {/* PODIUM (TOP 3) */}
                <div className="podium-section glass-panel">
                  <h2 className="section-heading text-center mb-8 flex items-center justify-center gap-3">
                    <Star className="text-[var(--secondary-gold)]" /> Mathura Computer Olympiad 2026 Champions <Star className="text-[var(--secondary-gold)]" />
                  </h2>
                  
                  <div className="podium-display">
                    {/* Rank 2 (Silver) */}
                    <div className="podium-step step-2">
                      <div className="podium-avatar silver-glow">
                        <Medal size={40} className="text-gray-300" />
                      </div>
                      <div className="podium-details">
                        <h4 className="winner-name">{mco2026Winners[1].name}</h4>
                        <p className="winner-school">{mco2026Winners[1].school}</p>
                        <span className="winner-score">Score: {mco2026Winners[1].score}</span>
                      </div>
                      <div className="podium-block block-2">2nd Place</div>
                    </div>

                    {/* Rank 1 (Gold) */}
                    <div className="podium-step step-1">
                      <div className="podium-avatar gold-glow">
                        <Trophy size={56} className="text-[var(--secondary-gold)]" />
                      </div>
                      <div className="podium-details">
                        <h4 className="winner-name text-[var(--secondary-gold)] text-xl">{mco2026Winners[0].name}</h4>
                        <p className="winner-school">{mco2026Winners[0].school}</p>
                        <span className="winner-score text-[var(--secondary-gold)] font-bold">Score: {mco2026Winners[0].score}</span>
                      </div>
                      <div className="podium-block block-1">1st Place</div>
                    </div>

                    {/* Rank 3 (Bronze) */}
                    <div className="podium-step step-3">
                      <div className="podium-avatar bronze-glow">
                        <Award size={36} className="text-amber-600" />
                      </div>
                      <div className="podium-details">
                        <h4 className="winner-name">{mco2026Winners[2].name}</h4>
                        <p className="winner-school">{mco2026Winners[2].school}</p>
                        <span className="winner-score">Score: {mco2026Winners[2].score}</span>
                      </div>
                      <div className="podium-block block-3">3rd Place</div>
                    </div>
                  </div>
                </div>

                {/* CONSOLATION PRIZE TABLE */}
                <div className="consolation-section glass-panel">
                  <h3 className="section-heading mb-6 flex items-center gap-2">
                    <Award className="text-blue-400" /> Consolation Prize Winners
                  </h3>
                  
                  <div className="table-responsive">
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Candidate Name</th>
                          <th>School Name</th>
                          <th className="text-right">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mco2026Consolation.map((student) => (
                          <tr key={student.rank}>
                            <td className="font-bold text-[var(--text-secondary)]">#{student.rank}</td>
                            <td className="font-semibold text-[var(--text-primary)]">{student.name}</td>
                            <td className="text-[var(--text-secondary)]">{student.school}</td>
                            <td className="text-right text-[var(--accent-cyan)] font-mono font-bold">{student.score}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
